import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { INR } from "@/lib/constants";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Receipt, Landmark, Wallet, Clock } from "lucide-react";

/* EOD Payments Queue — Super Admin only. Shows what NAMMA owes end-of-day:
   GST payable · Fee to Licensor · Expenses due today (+ overdue). */
export default function EODPaymentsPanel() {
  const { user } = useAuth();
  const [method, setMethod] = React.useState({});
  const qc = useQueryClient();
  const today = React.useMemo(() => new Date().toISOString().slice(0, 10), []);
  const { data, isLoading } = useQuery({
    queryKey: ["eod-payments", today],
    queryFn: async () => (await apiClient.get(`/finance/eod-payments?date=${today}`)).data,
    refetchInterval: 30000,
    enabled: user?.role === "SUPER_ADMIN",
  });
  const confirm = useMutation({
    mutationFn: async (payload) => (await apiClient.post("/finance/eod-payments/confirm", payload)).data,
    onSuccess: () => {
      toast.success("Payment confirmed ✓");
      qc.invalidateQueries({ queryKey: ["eod-payments"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (user?.role !== "SUPER_ADMIN") return null;
  if (isLoading || !data) return null;

  const gst = data.gst || { amount: 0, paid: false, outstanding: 0 };
  const lic = data.licensor || { amount: 0, paid: false, outstanding: 0 };
  const dueList = [...(data.expenses_due_today || []), ...(data.expenses_overdue || [])];

  const rowConfirm = (payload, key) => confirm.mutate({ ...payload, method: method[key] || "UPI" });

  return (
    <Panel data-testid="eod-payments-panel">
      <PanelHeader
        title="End-of-Day Payments"
        subtitle={`${today} · Total outstanding: ${INR(data.total_outstanding)}`}
        action={
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Confirmations today: <span className="text-volt font-bold">{data.confirmations_today?.length || 0}</span></div>
          </div>
        }
      />
      <div className="space-y-2">
        {/* GST */}
        <EODRow
          testid="eod-gst"
          icon={<Receipt size={14} className="text-volt" />}
          title="GST Payable"
          subtitle={`5% of today's ${data.payment_count} payments`}
          amount={gst.outstanding}
          totalAmount={gst.amount}
          paid={gst.paid}
          method={method["gst"] || "UPI"}
          onMethodChange={(v) => setMethod({ ...method, gst: v })}
          onConfirm={() => rowConfirm({ date: today, kind: "GST", amount: gst.outstanding, payee: "GST Department" }, "gst")}
          disabled={confirm.isPending || gst.outstanding <= 0}
        />
        {/* Licensor */}
        <EODRow
          testid="eod-licensor"
          icon={<Landmark size={14} className="text-volt" />}
          title="Fee to Licensor"
          subtitle="NAMMA Holding — per finance splits"
          amount={lic.outstanding}
          totalAmount={lic.amount}
          paid={lic.paid}
          method={method["lic"] || "BANK"}
          onMethodChange={(v) => setMethod({ ...method, lic: v })}
          onConfirm={() => rowConfirm({ date: today, kind: "LICENSOR", amount: lic.outstanding, payee: "NAMMA Holding" }, "lic")}
          disabled={confirm.isPending || lic.outstanding <= 0}
        />
        {/* Expenses due */}
        {dueList.length === 0 ? (
          <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 text-sm text-zinc-500" data-testid="eod-no-expenses">
            <Clock size={12} className="inline mr-1" /> No expenses due today.
          </div>
        ) : (
          dueList.map((e) => (
            <EODRow
              key={e.id}
              testid={`eod-exp-${e.id}`}
              icon={<Wallet size={14} className={e.due_date < today ? "text-red-400" : "text-volt"} />}
              title={`${e.category.replace(/_/g, " ")} · ${e.branch_name || ""}`}
              subtitle={`${e.notes || "Expense"} · Due ${e.due_date}${e.due_date < today ? " (OVERDUE)" : ""}`}
              amount={e.amount}
              totalAmount={e.amount}
              paid={false}
              overdue={e.due_date < today}
              method={method[e.id] || "UPI"}
              onMethodChange={(v) => setMethod({ ...method, [e.id]: v })}
              onConfirm={() => rowConfirm({ date: today, kind: "EXPENSE", reference_id: e.id, branch_id: e.branch_id, amount: e.amount, payee: e.notes || e.category }, e.id)}
              disabled={confirm.isPending}
            />
          ))
        )}
      </div>
    </Panel>
  );
}

function EODRow({ testid, icon, title, subtitle, amount, totalAmount, paid, overdue, method, onMethodChange, onConfirm, disabled }) {
  return (
    <div className={`bg-zinc-950 border rounded-sm p-3 flex items-center gap-3 flex-wrap ${overdue ? "border-red-500/40" : paid ? "border-green-500/40" : "border-zinc-900"}`} data-testid={testid}>
      {icon}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{subtitle}</div>
      </div>
      <div className="text-right">
        <div className="font-display text-2xl font-black text-volt">{INR(amount)}</div>
        {amount !== totalAmount && <div className="text-[10px] text-zinc-500 uppercase">of {INR(totalAmount)}</div>}
      </div>
      {paid ? (
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold text-green-400">
          <CheckCircle2 size={12} /> Paid
        </div>
      ) : (
        <>
          <Input value={method} onChange={(e) => onMethodChange(e.target.value)}
            data-testid={`${testid}-method`} placeholder="Method"
            className="w-24 bg-black border-zinc-800 rounded-sm text-xs" />
          <Button size="sm" onClick={onConfirm} disabled={disabled} data-testid={`${testid}-confirm`}
            className="bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.16em]">
            {overdue && <AlertCircle size={12} className="mr-1" />}
            <CheckCircle2 size={12} className="mr-1" /> Confirm Payment
          </Button>
        </>
      )}
    </div>
  );
}
